import { Popover, PopoverTrigger, PopoverContent,
  Select, SelectTrigger, SelectContent, SelectItem,
  ToggleGroup, ToggleGroupItem,
  Button,
  Label
} from '@ttab/elephant-ui'
import {
  ArrowDownNarrowWideIcon,
  ArrowUpDownIcon,
  ArrowUpWideNarrowIcon,
  Settings2Icon
} from '@ttab/elephant-ui/icons'
import { useMemo, useState } from 'react'
import { useTable } from '../hooks'
import { useTranslation } from 'react-i18next'

export const Sort = <TData,>() => {
  const { table } = useTable<TData>()
  const [open, setOpen] = useState(false)
  const [order, setOrder] = useState('asc')
  const [quickSort, setQuickSort] = useState<string[]>([])
  const { t } = useTranslation()

  const { grouping, sorting } = table.getState()

  const groupableColumns = useMemo(() =>
    table.getAllColumns()
      .filter((column) => column.getCanGroup()), [table])

  const sortableColumns = useMemo(() =>
    table.getAllColumns()
      .filter((column) => column.getCanSort()), [table])

  const handleQuickSortChange = (values: string[]) => {
    const value = values[0] ?? ''
    if (value === '') {
      table.setGrouping(['newsvalue'])
      table.setSorting([])
      setQuickSort([])
      return
    }
    table.setGrouping([value])
    table.setSorting([{ id: value, desc: value !== 'startTime' }])
    setQuickSort([value])
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger
        render={(
          <Button
            variant='ghost'
            size='xs'
            role='combobox'
            aria-expanded={open}
          >
            <ArrowUpDownIcon
              size={18}
              strokeWidth={1.75}
            />
          </Button>
        )}
      />
      <PopoverContent className='' align='start'>
        <div className='flex flex-col gap-4'>
          <div className='flex flex-row gap-4 items-center'>
            <Settings2Icon strokeWidth={1.75} size={18} />
            <ToggleGroup
              type='single'
              size='xs'
              value={quickSort}
              onValueChange={handleQuickSortChange}
            >
              {sortableColumns.find((column) => column.id === 'startTime')
                && (
                  <ToggleGroupItem
                    value='startTime'
                    aria-label={t('common:actions.sortBy', { label: t('core:labels.time').toLocaleLowerCase() })}
                    className='border text-muted-foreground data-[pressed]:text-accent-foreground'
                  >
                    {t('core:labels.time')}
                  </ToggleGroupItem>
                )}

              {sortableColumns.find((column) => column.id === 'newsvalue')
                && (
                  <ToggleGroupItem
                    value='newsvalue'
                    aria-label={t('common:actions.sortBy', { label: t('core:labels.newsvalue').toLocaleLowerCase() })}
                    className='border text-muted-foreground data-[pressed]:text-accent-foreground'
                  >
                    {t('core:labels.newsvalue')}
                  </ToggleGroupItem>
                )}

              {sortableColumns.find((column) => column.id === 'section')
                && (
                  <ToggleGroupItem
                    value='section'
                    aria-label={t('common:actions.sortBy', { label: t('core:labels.section').toLocaleLowerCase() })}
                    className='border text-muted-foreground data-[pressed]:text-accent-foreground'
                  >
                    {t('core:labels.section')}
                  </ToggleGroupItem>
                )}
            </ToggleGroup>
          </div>
          <Label htmlFor='grouping'>{t('common:actions.group')}</Label>
          <Select
            value={grouping[0]}
            onValueChange={(option) => {
              setQuickSort([])

              if (option === 'none') {
                table.setGrouping([])
                return
              }

              table.setGrouping([option])
            }}
          >
            <SelectTrigger>
              {groupableColumns.find((column) => column.id === grouping[0])?.columnDef.meta?.name || t('common:actions.grouping')}
            </SelectTrigger>
            <SelectContent id='grouping'>
              <SelectItem value='none'>{t('common:actions.nogrouping')}</SelectItem>
              {groupableColumns.map((column) => (
                <SelectItem value={column.id} key={column.id}>{column.columnDef.meta?.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Label htmlFor='sorting'>{t('common:actions.sort')}</Label>
          <div className='flex flex-row gap-2'>
            <Select
              value={sorting[0]?.id || 'none'}
              onValueChange={(option) => {
                setQuickSort([])

                if (option === 'none') {
                  table.setSorting([])
                  return
                }

                table.setSorting([{ id: option, desc: order === 'desc' }])
              }}
            >
              <SelectTrigger>
                {sortableColumns
                  .find((column) => column.id === sorting[0]?.id)
                  ?.columnDef.meta?.name || t('common:actions.presetSort')}
              </SelectTrigger>
              <SelectContent id='sorting'>
                <SelectItem value='none'>{t('common:actions.presetSort')}</SelectItem>
                {sortableColumns.map((column) => (
                  <SelectItem value={column.id} key={column.id}>{column.columnDef.meta?.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant='icon'
              disabled={sorting.length === 0}
              onClick={() => {
                setOrder(order === 'asc' ? 'desc' : 'asc')

                table.setSorting([{ id: sorting[0]?.id, desc: order === 'desc' }])
              }}
            >
              {order === 'asc'
                ? <ArrowDownNarrowWideIcon strokeWidth={1.75} size={18} />
                : <ArrowUpWideNarrowIcon strokeWidth={1.75} size={18} />}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
